import { getScript } from "@turo/libraries";

declare global {
  interface Window {
    devicer?: SocureDevicer;
  }
}

import config from "../config";

interface SocureDevicer {
  run(
    options: {
      context: string;
      endpoint: string;
      publicKey: string;
      userConsent: boolean;
    },
    callback: (response: SocureResponse) => void,
  ): void;
}

interface SocureResponse {
  [key: string]: unknown;
  sessionId?: string;
}

/**
 * Configuration for loading the Socure script using `getScript`
 */
const socureScriptOptions = {
  global: "devicer", // The global variable expected by Socure JS
  id: "socureJSId", // Unique script identifier
  isAsync: true, // Load script asynchronously
  url: "https://js.dvnfo.com/devicer.min.js", // URL to Socure's script
};

const deviceFPOptions = {
  context: "checkout", // Identifies the context in which the fingerprinting is being used
  endpoint: "https://fp.turo.com", // API endpoint for Socure device fingerprinting
  publicKey: config.SOCURE_API_KEY, // API key for authentication
  userConsent: true, // Ensures user consent is acknowledged
};

/**
 * SocureService using closure-based cache
 */
const SocureService = (() => {
  // Private variables for caching
  let cachedSessionId: string | null = null;
  let scriptPromise: Promise<SocureDevicer> | null = null;

  return {
    /**
     * Load the Socure session ID, either from cache or by loading the script and fetching it.
     */
    async getSocureSessionId(): Promise<string> {
      // If already cached, return the session ID
      if (cachedSessionId) {
        return cachedSessionId;
      }

      // Ensure the Socure library is loaded
      const devicer = await this.load();

      // Fetch the session ID
      return new Promise((resolve, reject) => {
        devicer.run(deviceFPOptions, (response: SocureResponse) => {
          if (response?.sessionId) {
            // Cache the session ID
            cachedSessionId = response.sessionId;
            resolve(cachedSessionId);
          } else {
            reject(new Error("Failed to fetch socureSessionId"));
          }
        });
      });
    },

    /**
     * Checks if the Socure script is already loaded.
     */
    isSocureLoaded(): boolean {
      return !!window.devicer || !!scriptPromise;
    },

    /**
     * Loads the Socure script asynchronously if it is not already loaded.
     */
    load(): Promise<SocureDevicer> {
      if (!this.isSocureLoaded()) {
        scriptPromise = getScript(socureScriptOptions);
      }
      // @ts-expect-error -- Update runtime code to narrow out `null` from `scriptPromise`
      return scriptPromise;
    },
  };
})();

export default SocureService;
